import { keepPreviousData } from '@tanstack/react-query';
import { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  DataObjectGroupType,
  errorMap,
  Form,
  getDayjs,
  Main,
  Spinner,
  Stack,
  useDataObjectGroupsWithDescriptionsQuery,
  useDirectory,
  useGetDataObjectRelationsGraph,
  useQueryParams,
  valueDate as getValueDate,
  dataObjectDescriptionMapping,
  ValidPeriodOptions,
  ISO_DATE_FORMAT,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './RelationsDirectory.translations.json';
import { Helmet } from 'react-helmet-async';
import { RelationsHeader } from './RelationsHeader';
import { z } from 'zod/v3';
import { useNavigateTo } from '../../paths/useNavigateTo';
import { path } from '../../paths';
import { Dayjs, isDayjs } from 'dayjs';
import { RelationsGraph } from './RelationsGraph';
import { RelationsLegend } from './RelationsLegend';
import { useFormContext, useWatch } from 'react-hook-form';

const { addTranslations, useTranslation, t } = i18nNsFactory(
  'relations-directory',
);
addTranslations(translations);

export interface RelationsDirectoryFilters {
  type?: string;
  groupOrDescriptionId?: string;
  valueDate?: string;
  version?: string;
  groupBy?: string;
  showOneStep?: boolean;
  showIntermediates?: boolean;
}

export const initialFilters: RelationsDirectoryFilters = {
  type: undefined,
  groupOrDescriptionId: undefined,
  valueDate: undefined,
  version: undefined,
  groupBy: undefined,
  showOneStep: false,
  showIntermediates: false,
};

export const FILTER_KEYS: (keyof RelationsDirectoryFilters)[] = [
  'type',
  'groupOrDescriptionId',
  'valueDate',
  'version',
  'groupBy',
  'showOneStep',
  'showIntermediates',
];

export const RelationsFilterSchema = z.object({
  type: z.string().optional().nullable(),
  groupOrDescriptionId: z
    .string(errorMap(() => t('validation.groupOrDescriptionId.required')))
    .min(1)
    .or(
      z
        .number(errorMap(() => t('validation.groupOrDescriptionId.required')))
        .min(1),
    ),
  valueDate: z.custom<Dayjs>(isDayjs, () => ({
    message: t('validation.valueDate.required'),
  })),
  version: z.number().or(z.string()).nullable().optional(),
  groupBy: z.array(z.string()).optional(),
  showOneStep: z.boolean(),
  showIntermediates: z.boolean(),
});

export const RelationsDirectory: FC = () => {
  const { t } = useTranslation();

  const navigate = useNavigateTo(path.relations);

  const handleSubmit = (values: Record<string, any>) => {
    navigate({
      ...values,
      valueDate: getDayjs(values.valueDate)!.format('YYYY-MM-DD'),
      type: values?.type ?? undefined,
      version: values?.version ?? undefined,
    });
  };
  const directoryProps = useDirectory({
    queryFilterKeys: FILTER_KEYS,
    initialFilters,
    arrayFields: ['groupBy'],
    booleanFields: ['showOneStep', 'showIntermediates'],
  });
  const { queryParameters } = directoryProps;
  const defaultValues = useMemo(() => {
    const valueDateParam = queryParameters?.valueDate;
    const showOneStepParam = queryParameters?.showOneStep;
    const showIntermediatesParam = queryParameters?.showIntermediates;
    const versionParam = queryParameters?.version;
    const groupOrDescriptionId = queryParameters?.groupOrDescriptionId;

    return {
      ...queryParameters,
      valueDate: valueDateParam ? getDayjs(valueDateParam) : undefined,
      showOneStep: showOneStepParam || false,
      showIntermediates: showIntermediatesParam || false,
      version: versionParam
        ? versionParam && versionParam !== 'latest'
          ? Number(versionParam)
          : versionParam
        : null,
      groupOrDescriptionId,
      groupBy: queryParameters?.groupBy
        ? Array.isArray(queryParameters?.groupBy)
          ? queryParameters.groupBy
          : [queryParameters.groupBy]
        : [],
    };
  }, [queryParameters]);

  return (
    <>
      <Helmet>
        <title>{t('title')}</title>
      </Helmet>
      <Main>
        <Main.Col>
          <Main.Header />
          <Main.Content padding={false} id="relations-content">
            <ContentWrapper>
              <Form
                schema={RelationsFilterSchema}
                onSubmit={handleSubmit}
                blockNavigationOnFirstChange={false}
                defaultValues={defaultValues}
              >
                <RelationsDirectoryInner queryParameters={queryParameters} />
              </Form>
            </ContentWrapper>
          </Main.Content>
          <Main.Footer>
            <RelationsLegend />
          </Main.Footer>
        </Main.Col>
      </Main>
    </>
  );
};

export const RelationsDirectoryInner: FC<{
  queryParameters: RelationsDirectoryFilters;
}> = ({ queryParameters }) => {
  const formValues = useWatch();
  const { type } = formValues;

  const {
    data: groupsWithDescriptions,
    isFetching: isFetchingGroupsWithDescriptions,
  } = useDataObjectGroupsWithDescriptionsQuery(
    {
      groupType: type
        ? type === 'INPUT'
          ? DataObjectGroupType.I
          : DataObjectGroupType.O
        : undefined,
      outputType: type
        ? dataObjectDescriptionMapping[
            type as keyof typeof dataObjectDescriptionMapping
          ]?.outputType
        : undefined,
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  const { setValue } = useFormContext();
  const [periodOptions, setPeriodOptions] = useState<ValidPeriodOptions>({});

  const [{ periodSkipLastValueDate }, setQueryParams] = useQueryParams();
  const closestValueDate = useMemo(
    () =>
      getValueDate(
        periodSkipLastValueDate
          ? getDayjs()
          : getDayjs(queryParameters?.valueDate),
        periodOptions.frequency,
        periodOptions.validRanges,
        periodSkipLastValueDate
          ? periodOptions.userFollowedGroupPeriodSkipCount
          : 0,
      )?.format(ISO_DATE_FORMAT),
    [
      periodOptions.frequency,
      periodOptions.userFollowedGroupPeriodSkipCount,
      periodOptions.validRanges,
      periodSkipLastValueDate,
      queryParameters?.valueDate,
    ],
  );

  useEffect(() => {
    if (!closestValueDate) return;
    const dayjsDate = getDayjs(closestValueDate);
    if (!dayjsDate?.isSame(queryParameters?.valueDate, 'day')) {
      setValue('valueDate', dayjsDate);
      if (
        queryParameters.groupOrDescriptionId !== formValues.groupOrDescriptionId
      ) {
        setQueryParams(
          { periodSkipLastValueDate: undefined },
          { replace: true },
        );
      }
    }
  }, [
    closestValueDate,
    formValues.groupOrDescriptionId,
    queryParameters?.valueDate,
    queryParameters.groupOrDescriptionId,
    setQueryParams,
    setValue,
  ]);

  const filters = useMemo(
    () => ({
      ...queryParameters,
      valueDate: closestValueDate,
    }),
    [closestValueDate, queryParameters],
  );

  const {
    data: graphData,
    isFetching,
    refetch,
  } = useGetDataObjectRelationsGraph(filters, {
    enabled:
      !!queryParameters.groupOrDescriptionId &&
      !!closestValueDate &&
      queryParameters.groupOrDescriptionId === formValues.groupOrDescriptionId,
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <RelationsHeader
        groupsWithDescriptions={groupsWithDescriptions}
        isFetchingGroupsWithDescriptions={isFetchingGroupsWithDescriptions}
        isFetchingData={isFetching}
        periodOptions={periodOptions}
        setPeriodOptions={setPeriodOptions}
      />
      {isFetching && <Spinner message={t('spinner')} />}
      {graphData && !isFetching && (
        <RelationsGraph graphData={graphData} refetchGraphData={refetch} />
      )}
    </>
  );
};

const ContentWrapper = styled(Stack)`
  width: 100%;
  max-width: 120em;
  height: 100%;
  margin-right: auto;
  margin-left: auto;
`;
